import React, { ChangeEvent, ReactNode, Ref, forwardRef } from 'react';
import { classNames } from '../../utils';
import { ChangeEventHandler } from 'react';
import { twMerge } from 'tailwind-merge';
import { FieldError, get, useForm, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

interface TextInputProps {
  type?: React.HTMLInputTypeAttribute;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  value?: number | string | null;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onSubmit?: (value: number | string | null) => void;
  onKeyDown?: (value: any) => void;
  className?: string;
  inputClassName?: string;
  tabIndex?: string;
  maxLength?: number;
  min?: number;
  max?: number;
  fieldErrorOverride?: FieldError;
  autoFocus?: boolean;
  submitOnEnter?: boolean;
  leftIconClassName?: string;
  rightIconClassName?: string;
  variant?: string;
  showBorder?: boolean;
}

// eslint-disable-next-line
export const TextInput: React.FC<TextInputProps> = (props) => {
  const {
    className = '',
    type,
    name,
    disabled,
    value,
    onBlur,
    onChange,
    onSubmit,
    onKeyDown,
    placeholder,
    leftIcon,
    rightIcon,
    inputClassName = '',
    maxLength,
    fieldErrorOverride,
    autoFocus,
    submitOnEnter,
    leftIconClassName,
    rightIconClassName,
    variant,
    showBorder = true,
  } = props;

  const methods = useFormContext();

  let errors = methods?.formState.errors;

  if (name && fieldErrorOverride) {
    errors = { [name]: fieldErrorOverride };
  }

  const error =
    fieldErrorOverride || (methods && name ? get(errors, name) : null);

  return (
    <>
      <div
        className={twMerge(
          'relative rounded-[4px] px-[12px]',
          disabled ? 'bg-sidewalk-30 cursor-default' : 'bg-white',
          error
            ? 'border-[1px] border-hydrant-500 bg-hydrant-30'
            : showBorder
              ? 'border-[1px] border-hardwood-300'
              : '',
          className
        )}
      >
        {leftIcon ? (
          <div
            className={twMerge(
              'pointer-events-none absolute inset-y-0 left-0 mt-[-2px] flex items-center pl-3',
              leftIconClassName
            )}
          >
            {leftIcon}
          </div>
        ) : null}

        <input
          autoFocus={autoFocus}
          maxLength={maxLength}
          disabled={disabled}
          // @ts-ignore
          value={value}
          type={type}
          name={name}
          onChange={onChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (submitOnEnter && onSubmit) {
                onSubmit(e.currentTarget.value);
              }
            }
            if (onKeyDown) {
              onKeyDown(e);
            }
          }}
          onBlur={onBlur}
          className={twMerge(
            'typo-body text-sidewalk-900 block h-[40px] w-full p-0 outline-none ring-0 placeholder:text-sidewalk-600  focus:shadow-none focus:ring-0',
            leftIcon ? 'pl-4' : '',
            rightIcon ? 'pr-4' : '',
            variant === 'underline'
              ? 'pl-0 h-6 border-t-0 border-l-0 border-r-0 border-b-[1px] border-b-morton-300 focus:border-b-morton-300'
              : 'border-none focus:border-none',
            disabled ? 'bg-sidewalk-30 text-sidewalk-500' : '',
            error
              ? variant === 'underline'
                ? 'border-t-0 border-l-0 border-r-0 border-b-[1px] border-b-hydrant-500'
                : 'bg-hydrant-30'
              : '',
            inputClassName
          )}
          placeholder={placeholder}
        />

        {rightIcon ? (
          <div
            className={twMerge(
              'pointer-events-none absolute inset-y-0 right-0 mt-[-2px] flex items-center pr-3',
              rightIconClassName
            )}
          >
            {rightIcon}
          </div>
        ) : null}
      </div>
      {name && error ? (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => {
            return (
              <div className="typo-body-small text-hydrant-500 absolute">
                {message}
              </div>
            );
          }}
        />
      ) : null}
    </>
  );
};
